import React, {
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSelector } from 'react-redux';
import { produce } from 'immer';
import _, { get, has, set } from 'lodash';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import { useLanguageSwitcherLocales } from '@bbkAdminComponents/designer/use-language-switcher-locales';
import { collectForwards } from '@bbkAdminComponents/experiences/offers/collect-forwards';
import { generateNewIdentityForOfferOrLossAversionCard } from '@bbkAdminComponents/experiences/offers/edit-offer-utils';
import { selectApp, selectCompany } from '@bbkAdminRedux/app/selectors';
import { type Integration } from '@bbkAdminRedux/data-management/reducers';
import { type BillingConfig } from '@bbkAdminRedux/rtkq/billing-config.slice';
import { useUploadBlobsToS3 } from '@bbkAdminRedux/rtkq/cdnAssets.slice';
import {
  useSelectAllFunnels,
  type FunnelType,
} from '@bbkAdminRedux/rtkq/funnels.slice';
import {
  genaiSlice,
  type EnrichArgs,
  type GenAIGeneratedContent,
} from '@bbkAdminRedux/rtkq/genai.slice';
import {
  offerConfigSlice,
  type OfferConfig,
} from '@bbkAdminRedux/rtkq/offer-config.slice';
import { offersAndLossAversionsSlice } from '@bbkAdminRedux/rtkq/offers-and-lossAversions.slice';
import {
  placementsSlice,
  useFetchWarningsForContent,
} from '@bbkAdminRedux/rtkq/placements.slice';
import { type FunnelPlacements } from '@bbkAdminRedux/rtkq/placements.utils';
import {
  parseRTKAPIError,
  type RTKAPIErrorResponse,
} from '@bbkAdminRedux/rtkq/rtkq-error-logger';
import { isLossAversionCard } from '@bbkAdminUtils/api-client/rest-cards-types';
import {
  isNewOffer,
  PublishState,
  type NewOffer,
  type RestOffer,
} from '@bbkAdminUtils/api-client/rest-offers-api';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { usePendoTrack, type TrackPayload } from '@bbkAdminUtils/usePendo';
import { type Dictionary } from '@bbkAdminUtils/utility-types';

export const EditOfferPageContext = createContext<{
  // Offer
  offer: RestOffer | NewOffer;
  setOffer: React.Dispatch<React.SetStateAction<RestOffer | NewOffer>>;
  initOffer: RestOffer | NewOffer;

  // Billing Integration
  billingIntegration: Integration | undefined;

  // Billing Config
  billingConfig: BillingConfig;

  // Offer Config
  offerConfig: OfferConfig;
  setOfferConfig: React.Dispatch<React.SetStateAction<OfferConfig>>;

  // Placements
  placementsMap: Dictionary<FunnelPlacements>;
  setPlacementsMap: React.Dispatch<
    React.SetStateAction<Dictionary<FunnelPlacements>>
  >;

  //AI-specific settings
  editedTitle: boolean;
  setEditedTitle: React.Dispatch<React.SetStateAction<boolean>>;
  visitedOfferEditorHome: boolean;
  setVisitedOfferEditorHome: React.Dispatch<React.SetStateAction<boolean>>;
  aiContentGenerated: boolean;
  setAiContentGenerated: React.Dispatch<React.SetStateAction<boolean>>;

  // Localization
  /** Locale currently being viewed/edited (base locale code when on base). */
  selectedLocale: string | undefined;
  /** Switches the editor to a locale, loading that locale's offer content. */
  setSelectedLocale: (locale: string) => void;
  /** Switches locale, or defers to a save/discard prompt if there are edits. */
  requestLocaleChange: (locale: string) => void;
  /** Locale awaiting confirmation while unsaved edits are resolved. */
  pendingLocale: string | undefined;
  clearPendingLocale: () => void;
  /** Non-base locale being edited, or undefined when on the base locale. */
  viewLocale: string | undefined;
  /** True while the selected locale's offer content is being fetched. */
  isLocaleLoading: boolean;

  // Misc
  isPristine: boolean;
  reset: () => void;
  previewDevice: PreviewDevice;
  setPreviewDevice: React.Dispatch<React.SetStateAction<PreviewDevice>>;

  // Async functions
  save: (
    publishState: PublishState,
    comment: string,
    checkWarnings: boolean
  ) => Promise<unknown>;
}>(undefined!);

type EditOfferPageProviderProps = {
  offer: RestOffer | NewOffer;
  billingConfig: BillingConfig;
  offerConfig: OfferConfig;
  placementsMap: Dictionary<FunnelPlacements>;
  billingIntegration: Integration | undefined;
  children: ReactNode;
  offerFunnelType: FunnelType;
};

export enum PreviewDevice {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

export enum MobileLayout {
  'TAKEOVER_MODAL' = 'takeover_modal',
  'PARTIAL_MODAL' = 'partial_modal',
}

export const EditOfferPageProvider: React.FC<EditOfferPageProviderProps> = ({
  offer: initOffer,
  billingConfig,
  offerConfig: initOfferConfig,
  placementsMap: initPlacementsMap,
  billingIntegration,
  children,
  offerFunnelType,
}) => {
  const company = useSelector(selectCompany);
  const companyId = company.internal_name;
  const app = useSelector(selectApp);
  const appKey = app.encoded_id;
  const activeFunnels = useSelectAllFunnels({
    appKey,
    funnelType: offerFunnelType,
  });
  const [createOffer] =
    offersAndLossAversionsSlice.endpoints.createOffer.useMutation();
  const [updateOffer] =
    offersAndLossAversionsSlice.endpoints.updateOffer.useMutation();
  const [fetchOfferForLocale] =
    offersAndLossAversionsSlice.endpoints.getOffer.useLazyQuery();
  const [updatePlacement] =
    placementsSlice.endpoints.updatePlacement.useMutation();
  const [putOfferConfig] =
    offerConfigSlice.endpoints.putOfferConfig.useMutation();
  const [enrichModelGeneratedContent] =
    genaiSlice.endpoints.enrich.useMutation();
  const [getModelGeneratedContent] =
    genaiSlice.endpoints.getContent.useLazyQuery();

  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>(
    PreviewDevice.DESKTOP
  );

  const uploadBlobsOnS3 = useUploadBlobsToS3();
  const fetchWarningsForContent = useFetchWarningsForContent();

  const [isPristine, setIsPristine] = useState<boolean>(true);

  const pendoTrack = usePendoTrack();

  // Offer
  const [offer, setOfferState] = useState(initOffer);
  const setOffer: typeof setOfferState = (...args) => {
    setIsPristine(false);
    setOfferState(...args);
  };

  // Localization
  // `baselineOffer` is the last server-loaded snapshot for the active locale; it
  // backs `reset()` and pristine tracking so reverting restores the locale the
  // user is editing rather than always the base offer.
  const [baselineOffer, setBaselineOffer] = useState(initOffer);
  const [primaryOffer, setPrimaryOffer] = useState(initOffer);
  const isMultiLanguage = useFeatureFlags(FeatureFlagsE.MULTI_LANGUAGE);
  const languageLocales = useLanguageSwitcherLocales({ enabledOnly: true });
  const primaryLocaleCode = useMemo(
    () => languageLocales.find((l) => l.isPrimary)?.code,
    [languageLocales]
  );
  const [selectedLocale, setSelectedLocaleState] = useState<string>();
  const [isLocaleLoading, setIsLocaleLoading] = useState(false);
  // Tracks the latest locale fetch so stale responses can be ignored.
  const localeRequestRef = useRef(0);
  useEffect(() => {
    if (!selectedLocale && primaryLocaleCode) {
      setSelectedLocaleState(primaryLocaleCode);
    }
  }, [selectedLocale, primaryLocaleCode]);
  const viewLocale =
    isMultiLanguage && selectedLocale && selectedLocale !== primaryLocaleCode
      ? selectedLocale
      : undefined;

  // Skip while viewing a translation so a base-offer refetch doesn't overwrite
  // the locale-scoped baseline backing reset()/revert.
  useEffect(() => {
    if (viewLocale) return;
    setBaselineOffer(initOffer);
    setPrimaryOffer(initOffer);
  }, [initOffer, viewLocale]);

  const setSelectedLocale = (locale: string) => {
    if (!isMultiLanguage) return;
    if (locale === selectedLocale) return;
    const isBase = !locale || locale === primaryLocaleCode;
    // New offers have no persisted content to translate; just track the selection.
    if (isNewOffer(initOffer)) {
      setSelectedLocaleState(locale);
      return;
    }
    const requestId = ++localeRequestRef.current;
    setIsLocaleLoading(true);
    fetchOfferForLocale({
      appKey,
      offerId: initOffer.id,
      ...(isBase ? {} : { locale }),
    })
      .unwrap()
      .then((loaded) => {
        if (localeRequestRef.current !== requestId) return; // superseded
        setBaselineOffer(loaded);
        setOfferState(loaded);
        if (isBase) setPrimaryOffer(loaded);
        setIsPristine(true);
        // Commit the locale only after its content loads.
        setSelectedLocaleState(locale);
      })
      .catch((e: RTKAPIErrorResponse) => {
        // On failure keep the previously selected locale.
        if (localeRequestRef.current !== requestId) return;
        const message = parseRTKAPIError(e).message;
        BlackToastTrigger({
          content: `Could not load ${locale} translation. ${message}`,
        });
      })
      .finally(() => {
        if (localeRequestRef.current !== requestId) return;
        setIsLocaleLoading(false);
      });
  };

  const [pendingLocale, setPendingLocale] = useState<string>();
  const clearPendingLocale = () => setPendingLocale(undefined);
  const requestLocaleChange = (locale: string) => {
    if (!isMultiLanguage) return;
    if (locale === selectedLocale) return;
    if (!isPristine) {
      // Defer until the user resolves the save/discard prompt.
      setPendingLocale(locale);
      return;
    }
    setSelectedLocale(locale);
  };

  // Offer Config
  const [offerConfig, setOfferConfigState] =
    useState<OfferConfig>(initOfferConfig);
  useEffect(() => setOfferConfigState(initOfferConfig), [initOfferConfig]);
  const setOfferConfig: typeof setOfferConfigState = (...args) => {
    setIsPristine(false);
    setOfferConfigState(...args);
  };

  // Placements
  const [placementsMap, setPlacementsMapState] = useState(initPlacementsMap);
  const setPlacementsMap: typeof setPlacementsMapState = (...args) => {
    setIsPristine(false);
    setPlacementsMapState(...args);
  };

  //AI-specific settings
  const [editedTitle, setEditedTitle] = useState<boolean>(false);
  const [visitedOfferEditorHome, setVisitedOfferEditorHome] =
    useState<boolean>(false);
  const [aiContentGenerated, setAiContentGenerated] = useState<boolean>(false);

  // Misc
  const reset = () => {
    setIsPristine(true);
    setOfferState(baselineOffer);
    setPlacementsMapState(initPlacementsMap);
  };

  const buildPendoTrackPayloadForAIOfferSave = (
    offer: RestOffer | NewOffer,
    outcome: EnrichArgs['outcome']
  ) =>
    ({
      event: `${isNewOffer(offer) ? 'Saving' : 'Updating'} AI offer${outcome === 'ACCEPTED_BUT_MODIFIED' ? ' with changes' : ''}`,
      properties: {
        offerId: offer.id,
        category: offer.category,
      },
    }) as TrackPayload;

  // Async functions
  const save = async (
    publishState: PublishState,
    comment: string,
    checkWarnings: boolean
  ) => {
    const updatedOffer = await uploadBlobsOnS3(offer);

    const activeFunnelsPlacementsMap: Dictionary<FunnelPlacements> = {};
    activeFunnels.forEach((funnel) => {
      if (funnel.active) {
        activeFunnelsPlacementsMap[funnel.id] = placementsMap[funnel.id];
      }
    });

    if (isMultiLanguage && viewLocale) {
      // Refetch primary before live publish.
      let primaryForPublish = primaryOffer;
      if (publishState === PublishState.live && !isNewOffer(initOffer)) {
        try {
          primaryForPublish = await fetchOfferForLocale({
            appKey,
            offerId: initOffer.id,
          }).unwrap();
          setPrimaryOffer(primaryForPublish);
        } catch (err) {
          const message = parseRTKAPIError(err as RTKAPIErrorResponse).message;
          return {
            type: 'error',
            message: `We could not load the primary offer for publish. Please try again. Error: ${message}`,
          };
        }
      }

      if (
        publishState === PublishState.live &&
        checkWarnings &&
        !isNewOffer(primaryForPublish)
      ) {
        const result = await fetchWarningsForContent(
          primaryForPublish,
          activeFunnelsPlacementsMap,
          offerFunnelType
        );
        if (result) {
          return { type: 'error', message: result };
        }
      }

      try {
        await updateOffer({
          appKey,
          publish_state: PublishState.draft,
          content: updatedOffer,
          comment,
          locale: viewLocale,
        }).unwrap();
        if (
          publishState === PublishState.live &&
          !isNewOffer(primaryForPublish)
        ) {
          await updateOffer({
            appKey,
            publish_state: PublishState.live,
            content: primaryForPublish,
            comment,
          }).unwrap();
        }
      } catch (err) {
        const message = parseRTKAPIError(err as RTKAPIErrorResponse).message;
        return {
          type: 'error',
          message: `We could not save the ${viewLocale} translation. Please try again. Error: ${message}`,
        };
      }
      setBaselineOffer(updatedOffer);
      setIsPristine(true);
      return { offerId: updatedOffer.id };
    }

    let offerToSave = updatedOffer;
    let updatedOfferId = null;
    let placementToSave = activeFunnelsPlacementsMap;
    const isNew = isNewOffer(updatedOffer);
    if (isNew) {
      const prevId = updatedOffer.id;
      const { cards } = updatedOffer;
      const updateCards = cards.map((card) => {
        const newCardIdentity = generateNewIdentityForOfferOrLossAversionCard(
          offer,
          card
        );
        return {
          ...card,
          newCardData: newCardIdentity,
        };
      });

      const updatedOfferWithRemappedNames = produce(updatedOffer, (draft) => {
        const { cards: updatedOfferCards } = draft;

        updatedOfferCards.forEach((card, index) => {
          if (has(card, 'requires.forwards.modal')) {
            const prevModal = get(card, 'requires.forwards.modal');
            const cardUpdate = updateCards.find((uc) => uc.name === prevModal);
            if (!cardUpdate || !cardUpdate?.newCardData.name) {
              GlobalLogger.error(
                `Broken reference to the modal: can't find card with name ${prevModal} ${JSON.stringify(card)} ${JSON.stringify(updateCards)} ${JSON.stringify(cardUpdate)}`
              );
            }
            set(card, 'requires.forwards.modal', cardUpdate?.newCardData.name);
          }
          if ('generic_actions' in card.requires) {
            card.requires.generic_actions?.forEach((ga) => {
              if (has(ga, 'modal')) {
                const cardUpdate = updateCards.find(
                  (uc) => uc.name === get(ga, 'modal')
                );
                set(ga, 'modal', cardUpdate?.newCardData.name);
              }
            });
          }
          if ('other_actions' in card.requires) {
            card.requires.other_actions?.forEach((oa) => {
              if (has(oa, 'modal')) {
                const cardUpdate = updateCards.find(
                  (uc) => uc.name === get(oa, 'modal')
                );
                set(oa, 'modal', cardUpdate?.newCardData.name);
              }
            });
          }
          const ctas = collectForwards(card);
          ctas?.forEach((draftCta) => {
            const ctaIdPath = [
              'cards',
              index,
              ...draftCta.path,
              'cta_details',
              'cta_id',
            ];
            set(card, ctaIdPath, undefined);
          });

          set(card, 'id', updateCards[index]?.newCardData.id);
          set(card, 'name', updateCards[index]?.newCardData.name);
        });

        set(draft, 'primary_card_name', draft.cards[0]?.name);
        set(draft, 'id', undefined);
      });

      const createdOffer = await createOffer({
        appKey,
        offer: updatedOfferWithRemappedNames,
      })
        .unwrap()
        .catch((e: RTKAPIErrorResponse) => {
          throw e.data;
        });

      const newId = createdOffer.id;
      updatedOfferId = newId;
      // update placements
      const newPlacements = produce(activeFunnelsPlacementsMap, (draft) => {
        Object.entries(draft).forEach(([funnelId, el]) => {
          if (!el) return;
          el.placements.forEach((p) => {
            if (get(p, 'content_id') === prevId) {
              set(p, 'content_id', newId);
            }
          });
        });
      });

      setOffer(createdOffer);
      setPlacementsMap(newPlacements);
      offerToSave = createdOffer;
      placementToSave = newPlacements;
    }

    //TODO AbK if offer content was model generated, then update contentID and outcome in OfferContent table for chosen record - also reject any remaining unchosen records for offer
    const primaryCardIdx = offer.cards.findIndex(
      (c) => c.name === offer.primary_card_name
    );
    const offerContentIdPath = ['cards', primaryCardIdx, 'offer_content_id'];
    const offerContentId = _.get(offerToSave, offerContentIdPath);
    if (!!offerContentId) {
      await getModelGeneratedContent({
        companyKey: companyId,
        appKey,
        id: offerContentId,
      })
        .unwrap()
        //has model generateed content
        .then(async (offerContentRecord) => {
          if (!offerContentRecord) return;
          const { outcome: initialOutcome, model_output } = offerContentRecord;
          if (initialOutcome === 'ACCEPTED_BUT_MODIFIED') {
            pendoTrack?.(
              buildPendoTrackPayloadForAIOfferSave(updatedOffer, initialOutcome)
            );
            return;
          }
          const {
            title,
            description,
            cta_text: buttonText,
          } = JSON.parse(model_output) as GenAIGeneratedContent;
          const primaryCard = offer.cards.at(primaryCardIdx)!;
          let outcome: EnrichArgs['outcome'] = initialOutcome;

          if (isLossAversionCard(primaryCard)) {
            const cardTitle = _.get(primaryCard, ['requires', 'title']);
            const cardDescription = _.get(primaryCard, ['requires', 'copy']);
            const cardButtonText = _.get(primaryCard, [
              'requires',
              'forwards',
              'text',
            ]);
            if (
              title !== cardTitle ||
              description !== cardDescription ||
              buttonText !== cardButtonText
            ) {
              outcome = 'ACCEPTED_BUT_MODIFIED';
            }
          } else {
            const cardTitle = _.get(primaryCard, ['requires', 'info', 'title']);
            const cardDescription = _.get(primaryCard, [
              'requires',
              'info',
              'details',
            ]);
            const cardButtonText = _.get(primaryCard, [
              'requires',
              'forwards',
              'text',
            ]);
            if (
              title !== cardTitle ||
              description !== cardDescription ||
              buttonText !== cardButtonText
            ) {
              outcome = 'ACCEPTED_BUT_MODIFIED';
            }
          }

          pendoTrack?.(
            buildPendoTrackPayloadForAIOfferSave(updatedOffer, outcome)
          );
          if (!outcome || outcome !== initialOutcome) {
            await enrichModelGeneratedContent({
              companyKey: companyId,
              appKey,
              content_id: offerToSave.id,
              offer_content_id: offerContentId,
              outcome: outcome ?? 'ACCEPTED',
            }).unwrap();
          }
        });
    }

    if (publishState === PublishState.live && checkWarnings) {
      const result = await fetchWarningsForContent(
        offerToSave,
        placementToSave,
        offerFunnelType
      );
      if (result) {
        return { type: 'error', message: result };
      }
    }

    // check that billing is present for the case where two keys are necessary
    if (!!billingIntegration && billingConfig?.manage_offers) {
      await putOfferConfig({
        app_key: appKey,
        offer_id: offerToSave.id,
        publish_state: publishState,
        ...offerConfig,
      })
        .unwrap()
        .catch((e: RTKAPIErrorResponse) => {
          const message = parseRTKAPIError(e).message;
          BlackToastTrigger({
            content: `Could not update offer configs ${message}`,
          });
        });
    }

    const funnelPlacementsThatChanged = _.chain(placementToSave)
      .values()
      .filter((fp) => !_.isUndefined(fp))
      .filter((fp) => {
        // This filter is answering the question:
        // Do we need to save this FunnelPlacements
        // We are just diffing placement in original and resulting placements
        const originalContentPlacement = offerToSave.placements.filter(
          (x) => x.funnel_id === fp.id
        );
        const resultContentPlacement = fp.placements.filter(
          (p) => p.content_id === offerToSave.id
        );
        return !_.isEqual(originalContentPlacement, resultContentPlacement);
      })
      .value();

    await Promise.all(
      funnelPlacementsThatChanged.map((el) =>
        updatePlacement({
          appKey,
          funnelId: el.id,
          publish_state: publishState,
          placements: el.placements,
          comment,
        })
      )
    );

    try {
      await updateOffer({
        appKey,
        publish_state: publishState,
        content: offerToSave,
        comment,
      }).unwrap();
    } catch (err) {
      // Surface the failure so callers don't switch locale or redirect (data loss).
      const message = parseRTKAPIError(err as RTKAPIErrorResponse).message;
      return {
        type: 'error',
        message: `We could not save the offer. Please try again. Error: ${message}`,
      };
    }

    setPrimaryOffer(offerToSave);
    return { offerId: updatedOfferId };
  };

  return (
    <EditOfferPageContext.Provider
      value={{
        // Offer
        offer,
        setOffer,
        previewDevice,
        setPreviewDevice,
        initOffer,

        // Billing Integration
        billingIntegration,

        // Billing Config
        billingConfig,

        // Offer Config
        offerConfig,
        setOfferConfig,

        // Placements
        placementsMap,
        setPlacementsMap,

        //AI-specific settings
        editedTitle,
        setEditedTitle,
        visitedOfferEditorHome,
        setVisitedOfferEditorHome,
        aiContentGenerated,
        setAiContentGenerated,

        // Localization
        selectedLocale,
        setSelectedLocale,
        requestLocaleChange,
        pendingLocale,
        clearPendingLocale,
        viewLocale,
        isLocaleLoading,

        // Misc
        isPristine,
        reset,

        // Async functions
        save,
      }}
    >
      {children}
    </EditOfferPageContext.Provider>
  );
};
